import { Input } from '@today/ui-primitives'
import { useId } from 'react'

/**
 * Props for {@link AttributeInput}.
 *
 * @property label - Accessible name, e.g. "IdP attribute for Given name".
 * @property value - The attribute name; empty when the rule is unmapped.
 * @property options - The sample's attribute names, offered as suggestions.
 * @property onChange - Reports the typed or picked name; `''` means cleared.
 * @property isInvalid - Marks the control as the target of a refusal.
 * @property isDisabled - Disables the control.
 */
export interface AttributeInputProps {
	label: string
	value: string
	options: string[]
	onChange: (value: string) => void
	isInvalid?: boolean
	isDisabled?: boolean
}

/**
 * The IdP-attribute combobox (REQ-203/REQ-204/REQ-205): suggests the sample's
 * attribute names and accepts free text, so a claim the sample lacks can still
 * be mapped. A native `<input list>` — exposed as `role="combobox"` — because
 * the `Combobox` atom only selects from its options.
 */
export function AttributeInput({
	label,
	value,
	options,
	onChange,
	isInvalid = false,
	isDisabled = false,
}: AttributeInputProps) {
	const listId = useId()
	return (
		<>
			<Input
				size="sm"
				aria-label={label}
				list={listId}
				value={value}
				disabled={isDisabled}
				aria-invalid={isInvalid || undefined}
				onChange={(event) => onChange(event.target.value)}
			/>
			<datalist id={listId}>
				{options.map((name) => (
					<option key={name} value={name} />
				))}
			</datalist>
		</>
	)
}
